import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { cn } from '../../utils/cn';
import { CHIP_DENSE } from '../../utils/chipColors';
import type { Anomaly } from '../../utils/scheduleAnomalies';

interface AnomalyBannerProps {
  anomalies: Anomaly[];
  /**
   * Optional click handler. When provided, anomalies whose
   * `statusFilter` is non-empty render as buttons that dispatch the
   * status-filter values. Anomalies with empty `statusFilter` always
   * render as non-clickable badges.
   *
   * Omit the handler entirely (Positions view) → all chips render as
   * badges. The banner still appears, just informational.
   */
  onChipClick?: (statusFilter: Anomaly['statusFilter']) => void;
}

// Bg + text from canonical CHIP_DENSE; border per-severity since this surface
// uses a thin border in addition to the bg.
const SEVERITY_BADGE_CLS: Record<Anomaly['severity'], string> = {
  error:   `${CHIP_DENSE.REJECTED} border-red-300 dark:border-red-800`,
  warning: `${CHIP_DENSE.EXIT_PENDING} border-amber-300 dark:border-amber-800`,
};

const SEVERITY_BUTTON_HOVER_CLS: Record<Anomaly['severity'], string> = {
  error:   'hover:bg-red-200 dark:hover:bg-red-900/50',
  warning: 'hover:bg-amber-200 dark:hover:bg-amber-900/50',
};

/**
 * Banner that surfaces non-routine status counts as a row of chips so
 * traders can spot trouble at a glance instead of scanning the full
 * status strip. Hidden entirely when there are no anomalies — clean
 * days don't deserve chrome.
 *
 * Sits between the ProHeroBanner (overview) and SharedToolbar
 * (table-scope controls). Each chip can dispatch a column-filter
 * update via `onChipClick` so the user lands directly on the affected
 * rows.
 */
const AnomalyBanner: React.FC<AnomalyBannerProps> = ({ anomalies, onChipClick }) => {
  if (anomalies.length === 0) return null;

  return (
    <div
      role="alert"
      aria-label="Schedule anomalies"
      className="flex items-center gap-2 flex-wrap rounded-lg border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/10 px-3 py-2"
    >
      <AlertTriangle
        aria-hidden="true"
        className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0"
      />
      <span className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-300">
        Needs attention
      </span>
      <span aria-hidden="true" className="text-amber-300 dark:text-amber-700">·</span>
      {anomalies.map((a) => {
        const clickable = onChipClick && a.statusFilter.length > 0;
        const baseCls = cn(
          'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border tabular-nums',
          SEVERITY_BADGE_CLS[a.severity],
        );
        if (clickable) {
          return (
            <button
              key={a.key}
              type="button"
              data-anomaly-key={a.key}
              onClick={() => onChipClick(a.statusFilter)}
              className={cn(baseCls, 'transition-colors', SEVERITY_BUTTON_HOVER_CLS[a.severity])}
              title={`Filter to ${a.label}`}
            >
              {a.label}
            </button>
          );
        }
        return (
          <span
            key={a.key}
            data-anomaly-key={a.key}
            className={baseCls}
            title={a.label}
          >
            {a.label}
          </span>
        );
      })}
    </div>
  );
};

export default AnomalyBanner;
